// Custom Hook for POC Management
import { useState, useCallback, useEffect } from 'react';
import {
  fetchPOCStatistics,
  checkPOCExists,
  fetchPOCs,
  fetchAmplifyPOCs,
  fetchPOCById,
  fetchAmplifyPOCById,
  createPOC,
  createAmplifyPOC,
  updatePOC,
  updateAmplifyPOC,
  deletePOC,
  deleteAmplifyPOC,
  syncPOCToAmplify
} from '@/services/superAdminAPI';

/**
 * Custom hook for managing POCs in the admin dashboard
 */
export const usePOCManagement = () => {
  // State Management
  const [pocs, setPOCs] = useState([]);
  const [amplifyPOCs, setAmplifyPOCs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [totalPOCs, setTotalPOCs] = useState(0);
  const [totalAmplifyPOCs, setTotalAmplifyPOCs] = useState(0);
  
  // Loading States
  const [isLoading, setIsLoading] = useState(false);
  const [isStatisticsLoading, setIsStatisticsLoading] = useState(false);
  
  // Error State
  const [error, setError] = useState(null);
  
  // Pagination & Filter State
  const [pocFilters, setPOCFilters] = useState({
    page: 1,
    page_size: 20,
    search: '',
    region: '',
    country: ''
  });

  const [amplifyFilters, setAmplifyFilters] = useState({
    page: 1,
    page_size: 20,
    search: '',
    region: '',
    country: ''
  });

  /**
   * Helper function to handle API errors
   */
  const handleError = useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'An unexpected error occurred';
    
    setError({
      message: errorMessage,
      context,
      timestamp: new Date().toISOString()
    });

    // Auto-clear error after 5 seconds
    setTimeout(() => setError(null), 5000);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Load POC statistics
   */
  const loadStatistics = useCallback(async () => {
    try {
      setIsStatisticsLoading(true);
      setError(null);
      const stats = await fetchPOCStatistics();
      setStatistics(stats);
      return stats;
    } catch (err) {
      handleError(err, 'loadStatistics');
      throw err;
    } finally {
      setIsStatisticsLoading(false);
    }
  }, [handleError]);

  /**
   * Load POCs with current filters
   */
  const loadPOCs = useCallback(async (filters = pocFilters) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchPOCs(filters);
      setPOCs(response.data || []);
      setTotalPOCs(response.total || 0);
      return response;
    } catch (err) {
      handleError(err, 'loadPOCs');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [pocFilters, handleError]);

  /**
   * Load Amplify POCs with current filters
   */
  const loadAmplifyPOCs = useCallback(async (filters = amplifyFilters) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchAmplifyPOCs(filters);
      setAmplifyPOCs(response.data || []);
      setTotalAmplifyPOCs(response.total || 0);
      return response;
    } catch (err) {
      handleError(err, 'loadAmplifyPOCs');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [amplifyFilters, handleError]);

  /**
   * Check if POC exists
   */
  const checkExists = useCallback(async (pocId) => {
    try {
      setError(null);
      return await checkPOCExists(pocId);
    } catch (err) {
      handleError(err, 'checkExists');
      throw err;
    }
  }, [handleError]);

  /**
   * Get POC by ID
   */
  const getPOCById = useCallback(async (pocId) => {
    try {
      setError(null);
      return await fetchPOCById(pocId);
    } catch (err) {
      handleError(err, 'getPOCById');
      throw err;
    }
  }, [handleError]);

  /**
   * Get Amplify POC by ID
   */
  const getAmplifyPOCById = useCallback(async (pocId) => {
    try {
      setError(null);
      return await fetchAmplifyPOCById(pocId);
    } catch (err) {
      handleError(err, 'getAmplifyPOCById');
      throw err;
    }
  }, [handleError]);

  /**
   * Create a new POC
   */
  const addPOC = useCallback(async (pocData) => {
    try {
      setError(null);
      const result = await createPOC(pocData);
      
      // Refresh POC list
      await loadPOCs();
      await loadStatistics();
      
      return result;
    } catch (err) {
      handleError(err, 'addPOC');
      throw err;
    }
  }, [loadPOCs, loadStatistics, handleError]);

  /**
   * Create a new Amplify POC
   */
  const addAmplifyPOC = useCallback(async (pocData) => {
    try {
      setError(null);
      const result = await createAmplifyPOC(pocData);
      
      // Refresh lists
      await loadAmplifyPOCs();
      await loadStatistics();
      
      return result;
    } catch (err) {
      handleError(err, 'addAmplifyPOC');
      throw err;
    }
  }, [loadAmplifyPOCs, loadStatistics, handleError]);

  /**
   * Update a POC
   */
  const editPOC = useCallback(async (pocId, pocData) => {
    try {
      setError(null);
      const result = await updatePOC(pocId, pocData);
      
      // Update local state
      setPOCs(prevPOCs => 
        prevPOCs.map(poc => 
          poc.id === pocId ? { ...poc, ...result } : poc
        )
      );
      
      return result;
    } catch (err) {
      handleError(err, 'editPOC');
      throw err;
    }
  }, [handleError]);

  /**
   * Update an Amplify POC
   */
  const editAmplifyPOC = useCallback(async (pocId, pocData) => {
    try {
      setError(null);
      const result = await updateAmplifyPOC(pocId, pocData);
      
      // Update local state
      setAmplifyPOCs(prevPOCs => 
        prevPOCs.map(poc => 
          poc.id === pocId ? { ...poc, ...result } : poc
        )
      );
      
      return result;
    } catch (err) {
      handleError(err, 'editAmplifyPOC');
      throw err;
    }
  }, [handleError]);

  /**
   * Delete a POC
   */
  const removePOC = useCallback(async (pocId) => {
    try {
      setError(null);
      const result = await deletePOC(pocId);
      
      // Remove from local state
      setPOCs(prevPOCs => prevPOCs.filter(poc => poc.id !== pocId));
      setTotalPOCs(prev => prev - 1);
      
      await loadStatistics();
      
      return result;
    } catch (err) {
      handleError(err, 'removePOC');
      throw err;
    }
  }, [loadStatistics, handleError]);

  /**
   * Delete an Amplify POC
   */
  const removeAmplifyPOC = useCallback(async (pocId) => {
    try {
      setError(null);
      const result = await deleteAmplifyPOC(pocId);
      
      // Remove from local state
      setAmplifyPOCs(prevPOCs => prevPOCs.filter(poc => poc.id !== pocId));
      setTotalAmplifyPOCs(prev => prev - 1);
      
      await loadStatistics();
      
      return result;
    } catch (err) {
      handleError(err, 'removeAmplifyPOC');
      throw err;
    }
  }, [loadStatistics, handleError]);

  /**
   * Sync POC to Amplify
   */
  const syncToAmplify = useCallback(async (pocId, traditionalBudget = 0) => {
    try {
      setError(null);
      const result = await syncPOCToAmplify(pocId, traditionalBudget);
      
      // Refresh both lists
      await loadPOCs();
      await loadAmplifyPOCs();
      await loadStatistics();
      
      return result;
    } catch (err) {
      handleError(err, 'syncToAmplify');
      throw err;
    }
  }, [loadPOCs, loadAmplifyPOCs, loadStatistics, handleError]);

  /**
   * Update POC filters
   */
  const updatePOCFilters = useCallback((newFilters) => {
    setPOCFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Update Amplify filters
   */
  const updateAmplifyFilters = useCallback((newFilters) => {
    setAmplifyFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setPOCFilters({
      page: 1,
      page_size: 20,
      search: '',
      region: '',
      country: ''
    });
    setAmplifyFilters({
      page: 1,
      page_size: 20,
      search: '',
      region: '',
      country: ''
    });
  }, []);

  /**
   * Refresh all data
   */
  const refreshData = useCallback(async () => {
    try {
      await Promise.all([
        loadStatistics(),
        loadPOCs(),
        loadAmplifyPOCs()
      ]);
    } catch (err) {
      console.error('Error refreshing data:', err);
    }
  }, [loadStatistics, loadPOCs, loadAmplifyPOCs]);

  return {
    // State
    pocs,
    amplifyPOCs,
    statistics,
    totalPOCs,
    totalAmplifyPOCs,
    pocFilters,
    amplifyFilters,
    error,
    
    // Loading States
    isLoading,
    isStatisticsLoading,
    
    // Data Loading Functions
    loadStatistics,
    loadPOCs,
    loadAmplifyPOCs,
    checkExists,
    getPOCById,
    getAmplifyPOCById,
    refreshData,
    
    // POC Management Functions
    addPOC,
    addAmplifyPOC,
    editPOC,
    editAmplifyPOC,
    removePOC,
    removeAmplifyPOC,
    syncToAmplify,
    
    // Filter Management
    updatePOCFilters,
    updateAmplifyFilters,
    clearFilters,
    
    // Error Management
    clearError
  };
};

export default usePOCManagement;
